/**
 * RecommendationActionModal — three-mode modal for resolving Command Dashboard
 * recommendations against a marketing channel (or any partner-style entity).
 *
 * Modes:
 *   • apply    — capture the decision (amount, effective date, notes) and log
 *                a `decision_applied` event to channel history.
 *   • dismiss  — capture a required reason + optional snooze date and log a
 *                `dismissal` event. Dismissed recs are suppressed on the
 *                dashboard until snooze expires (or indefinitely if unset).
 *   • touch    — log a freeform contact / note (call to account rep, etc.)
 *                without applying or dismissing the recommendation.
 *
 * Beta safety: no destructive ops; everything appends to in-memory history.
 */

import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addChannelEvent, type ChannelEventType } from "@/data/channelRelationship";
import { toast } from "sonner";

export type RecActionMode = "apply" | "dismiss" | "touch";

export interface RecActionContext {
  channelId: string;
  channelName: string;
  recommendationKey: string;
  recommendationText: string;
  decisionLabel: string;       // e.g. "Increase budget"
  severity: "positive" | "neutral" | "caution";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RecActionMode;
  context: RecActionContext | null;
  actor?: string;
}

const SNOOZE_OPTIONS: { value: string; label: string; days: number | null }[] = [
  { value: "7", label: "7 days", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "90", label: "Quarter (90 days)", days: 90 },
  { value: "indefinite", label: "Indefinitely", days: null },
];

const DISMISS_REASONS = [
  "Already actioned this period",
  "Disagree with recommendation",
  "Waiting on partner response",
  "Out of budget right now",
  "Strategic — revisit later",
  "Other",
];

export function RecommendationActionModal({
  open, onOpenChange, mode, context, actor = "Mike Reynolds",
}: Props) {
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState("");
  const [amount, setAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [dismissReason, setDismissReason] = useState(DISMISS_REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [snooze, setSnooze] = useState("30");

  // Reset on open
  useEffect(() => {
    if (open) {
      setNotes("");
      setContact("");
      setAmount("");
      setEffectiveDate(new Date().toISOString().slice(0, 10));
      setDismissReason(DISMISS_REASONS[0]);
      setCustomReason("");
      setSnooze("30");
    }
  }, [open]);

  if (!context) return null;

  const titleByMode: Record<RecActionMode, string> = {
    apply: `Apply: ${context.decisionLabel}`,
    dismiss: `Dismiss recommendation`,
    touch: `Log touch — ${context.channelName}`,
  };
  const ctaByMode: Record<RecActionMode, string> = {
    apply: "Apply & log decision",
    dismiss: "Dismiss & log reason",
    touch: "Save touch",
  };
  const eventTypeByMode: Record<RecActionMode, ChannelEventType> = {
    apply: "decision_applied",
    dismiss: "dismissal",
    touch: "touch",
  };

  const canSubmit = (() => {
    if (mode === "apply") return notes.trim().length > 0;
    if (mode === "dismiss") {
      const reason = dismissReason === "Other" ? customReason.trim() : dismissReason;
      return reason.length > 0;
    }
    return notes.trim().length > 0;
  })();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const reasonText = dismissReason === "Other" ? customReason.trim() : dismissReason;

    let snoozeUntil: string | undefined;
    if (mode === "dismiss") {
      const opt = SNOOZE_OPTIONS.find((o) => o.value === snooze);
      if (opt?.days != null) {
        const d = new Date();
        d.setDate(d.getDate() + opt.days);
        snoozeUntil = d.toISOString();
      }
      // "indefinite" → snoozeUntil stays undefined
    }

    addChannelEvent({
      channelId: context.channelId,
      channelName: context.channelName,
      eventType: eventTypeByMode[mode],
      recommendationKey: context.recommendationKey,
      recommendationText: context.recommendationText,
      decisionLabel: mode === "apply" ? context.decisionLabel : undefined,
      notes: mode === "dismiss" ? (notes.trim() || reasonText) : notes.trim(),
      contact: contact.trim() || undefined,
      amount: amount ? Number(amount) : undefined,
      effectiveDate: mode === "apply" && effectiveDate ? effectiveDate : undefined,
      dismissReason: mode === "dismiss" ? reasonText : undefined,
      snoozeUntil,
      actor,
    });

    const successMsg =
      mode === "apply" ? `Decision logged for ${context.channelName}` :
      mode === "dismiss" ? `Recommendation dismissed${snoozeUntil ? ` until ${new Date(snoozeUntil).toLocaleDateString()}` : ""}` :
      `Touch logged for ${context.channelName}`;
    toast.success(successMsg);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base">{titleByMode[mode]}</DialogTitle>
          <DialogDescription className="text-xs">
            {context.recommendationText}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {mode === "apply" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rec-amount" className="text-xs">Amount (optional)</Label>
                  <Input
                    id="rec-amount"
                    type="number"
                    placeholder="e.g. 1500"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rec-effective" className="text-xs">Effective date</Label>
                  <Input
                    id="rec-effective"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rec-contact" className="text-xs">Partner contact (optional)</Label>
                <Input
                  id="rec-contact"
                  placeholder="e.g. Sarah at Meta"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                />
              </div>
            </>
          )}

          {mode === "dismiss" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason</Label>
                <Select value={dismissReason} onValueChange={setDismissReason}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISMISS_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {dismissReason === "Other" && (
                <div className="space-y-1.5">
                  <Label htmlFor="rec-custom-reason" className="text-xs">Custom reason</Label>
                  <Input
                    id="rec-custom-reason"
                    placeholder="Brief reason"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Snooze</Label>
                <Select value={snooze} onValueChange={setSnooze}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SNOOZE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Recommendation will be suppressed on the dashboard during the snooze window.
                </p>
              </div>
            </>
          )}

          {mode === "touch" && (
            <div className="space-y-1.5">
              <Label htmlFor="rec-touch-contact" className="text-xs">Contact (optional)</Label>
              <Input
                id="rec-touch-contact"
                placeholder="e.g. Sarah at Meta — quarterly review call"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="rec-notes" className="text-xs">
              {mode === "dismiss" ? "Additional notes (optional)" : "Notes"}
              {mode !== "dismiss" && <span className="text-destructive"> *</span>}
            </Label>
            <Textarea
              id="rec-notes"
              placeholder={
                mode === "apply" ? "What did you change and why?" :
                mode === "dismiss" ? "Optional context for the audit trail" :
                "What was discussed / observed?"
              }
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {ctaByMode[mode]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
